import React, { CSSProperties } from 'react';
import { success } from 'assets/colors/colors';
import type { ILeaveType } from 'types/Leaves/leave';
import type { IProjectOption } from 'types/Project/project';
/** Project status value for completed projects (used in labels) */
const PROJECT_STATUS_COMPLETED = 3;

/**
 * Generates a label for a task by combining the WBS (Work Breakdown Structure) ID and the task title.
 *
 * @param wbs_id - The WBS ID to be included in the label.
 * @param title - The title of the task.
 * @returns A string that combines the WBS ID and the title, formatted as "(WBS_ID) Title" if the WBS ID is provided, otherwise just the title.
 */
export const getTaskLabel = (wbs_id: string, title: string) =>
  wbs_id ? `(${wbs_id}) ${title}` : title;

/**
 * Generates a label for an accounting code by concatenating the code, description, and archived status.
 *
 * @param accounting_code - The accounting code to be labeled.
 * @param description - A description of the accounting code.
 * @param archived - A boolean indicating if the accounting code is archived.
 * @returns A string representing the formatted label for the accounting code.
 */
export const getAccountingCodeLabel = (
  accounting_code: string,
  description: string,
  archived: boolean,
) =>
  accounting_code +
  (description ? ` - ${description}` : '') +
  (archived ? '(Archived)' : '');

/**
 * Resource
 */
export const getResourceLabel = (
  resourceCode: string,
  resourceName: string,
  archived?: boolean,
) =>
  resourceCode && resourceName
    ? `(${resourceCode}) ${resourceName} ${archived ? '(Archived)' : ''}`
    : '';

/**
 * Cost Centre
 * @param code
 * @param name
 * @returns
 */
export const getCostCentreLabel = (
  code: string | undefined,
  name: string | undefined,
  archived?: boolean,
) => {
  const baseLabel = code && name ? `(${code}) ${name}` : name;
  return archived ? `${baseLabel} (Archived)` : baseLabel;
};

export const getProjectLabel = (jobId: string, name: string) =>
  jobId && name ? `(${jobId}) ${name}` : name;

export const getProjectLabelWithStatus = (option: IProjectOption): string =>
  `(${option?.job_id}) ${option?.name} ${
    option?.status === PROJECT_STATUS_COMPLETED ? '(Completed)' : ''
  }`;

export interface GetProjectLabelWithStatusElementParams {
  option: IProjectOption;
}

/**
 * Returns JSX for project label with "(Completed)" in green when status is completed.
 * Use where the label is rendered as JSX (e.g. dropdown options).
 */
export const getProjectLabelWithStatusElement = ({
  option,
}: GetProjectLabelWithStatusElementParams): React.ReactElement => (
  <>
    ({option?.job_id}) {option?.name}
    {option?.status === PROJECT_STATUS_COMPLETED && (
      <span style={{ color: success.main }}> (Completed)</span>
    )}
  </>
);

/**
 * Employee
 */
export const getEmployeeLabel = (firstName: string, lastName: string) =>
  `${firstName} ${lastName}`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildLabelParts = ({
  name,
  idLabel,
  isActive = false,
  reverseOrder = false,
}: {
  name: string;
  idLabel: string;
  isActive?: boolean;
  reverseOrder?: boolean;
}): string => {
  const archivedSuffix = isActive ? '' : '(Archived)';
  const parts = reverseOrder
    ? [idLabel, name, archivedSuffix]
    : [name, idLabel, archivedSuffix];

  return parts.filter(Boolean).join(' ').trim();
};

export const getEmployeeLabelWithStatus = ({
  firstName,
  lastName,
  id,
  isActive,
  style,
  returnString = false,
  reverseOrder = false,
}: {
  firstName: string;
  lastName: string;
  id?: string;
  isActive?: boolean;
  style?: CSSProperties;
  returnString?: boolean;
  reverseOrder?: boolean;
}) => {
  if (!firstName) {
    return null;
  }
  const name = `${firstName} ${lastName}`.trim();
  const idLabel = id ? `(${id})` : '';

  if (returnString) {
    return buildLabelParts({ name, idLabel, isActive, reverseOrder });
  }

  return (
    <>
      {reverseOrder ? (
        <>
          {idLabel} {name}
        </>
      ) : (
        <>
          {name} {idLabel}
        </>
      )}
      {!isActive && <span style={style}> (Archived)</span>}
    </>
  );
};

export const getEquipmentLabel = (
  equipmentName: string,
  equipmentId: string,
  reverseOrder = false,
) =>
  reverseOrder
    ? `(${equipmentId}) ${equipmentName}`
    : `${equipmentName} (${equipmentId})`;

export const getEquipmentLabelWithArchived = ({
  equipmentName,
  equipmentId,
  archived,
}: {
  equipmentName: string;
  equipmentId: string;
  archived: boolean;
}) => `${equipmentName} (${equipmentId}) ${archived ? '(Archived)' : ''}`;
/**
 * Generates a user label by combining the first name, last name, and optional role.
 *
 * @param firstName - The user's first name.
 * @param lastName - The user's last name.
 * @param role - (Optional) The user's role.
 * @returns A formatted string representing the user label.
 */
export const getUserLabel = (
  firstName: string,
  lastName: string,
  role?: string,
): string => {
  const roleLabel = role ? `(${role})` : '';
  return `${firstName} ${lastName} ${roleLabel}`.trim();
};

export const getUserLabelWithEmail = ({
  firstName,
  lastName,
  fullName,
  email,
}: {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
}) => {
  const name = fullName || `${firstName} ${lastName}`;
  const emailLabel = email ? `(${email})` : '';
  return `${name} ${emailLabel}`.trim();
};

/**
 * Generates a label for a leave type by combining the name or display name with archived status.
 *
 * @param name - The name of the leave type.
 * @param display_name - The display name of the leave type (optional).
 * @param is_archived - A boolean indicating if the leave type is archived.
 * @returns A string representing the formatted label for the leave type, with "(archived)" suffix if archived.
 */
export const getLeaveTypeOptionLabel = ({
  name,
  display_name,
  is_archived,
  style,
  asString = false,
}: Pick<ILeaveType, 'name' | 'display_name' | 'is_archived'> & {
  style?: CSSProperties;
  asString?: boolean;
}) => {
  const label = display_name || name;
  if (asString) {
    return is_archived ? `${label} (Archived)` : label;
  }
  return (
    <React.Fragment>
      {label} {is_archived ? <span style={style}>(Archived)</span> : null}
    </React.Fragment>
  );
};

export const getSupplierLabel = (supplierName: string, isArchived: boolean) =>
  isArchived ? `${supplierName} (Archived)` : supplierName;
